"use client"

import type React from "react"

import { useState, useEffect, useMemo } from "react"
import {
  LuMapPin,
  LuPlus,
  LuLoader,
  LuNetwork,
  LuInfo,
  LuUser,
  LuGlobe,
  LuCheck,
  LuChevronRight,
  LuFileText,
  LuClipboard,
  LuBuilding,
  LuPhone,
} from "react-icons/lu"
import { SelectWithSearch, SelectWithSearch2 } from "../../utils/select-with-search"
import axiosInstance from "../../libs/axios"
import { useNavigate } from "react-router-dom"
import validator from "validator"
import { countries } from "../../utils/countries"
import { uploadFilesWithPresignedUrls } from "../../libs/presigned-upload"

interface Client {
  id: number
  client_name: string
  status: string
}

interface Activity {
  id: number
  activity_name: string
  status: string
}

interface Project {
  id: number
  project_name: string
  status: string
  client?: Client
  activity?: Activity
}

interface AddSiteData {
  site_id: string
  project_id: number
  activity_id?: number
  reference_number?: string
  client_allocation_date?: string
  site_name?: string
  site_address?: string
  opco_name?: string
  opco_id?: string
  state_circle_id?: number
  district_id?: number
  pincode?: string
  site_latitude?: number
  site_longitude?: number
  site_priority?: string
  pr_number?: string
  pr_date?: string
  po_number?: string
  po_date?: string
  po_files?: string[]
  is_po_no_shared_at_allocation?: boolean
  technician_name?: string
  technician_number?: string
  fse_name?: string
  fse_number?: string
  aom_name?: string
  aom_number?: string
  client_project_name?: string
  customer_requirement?: string
  site_information1?: string
  site_information2?: string
  site_information3?: string
  contact_person?: string
  contact_number?: string
  site_engineer_userid?: number
  ta_userid?: number
  mis_userid?: number
  report_designer_userid?: number
  validator_userid?: number
}

const CreateSite = ({ }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1)
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [formData, setFormData] = useState<AddSiteData>({
    site_id: "",
    project_id: 0,
    activity_id: undefined,
    reference_number: "",
    client_allocation_date: "",
    site_name: "",
    site_address: "",
    opco_name: "",
    opco_id: "",
    state_circle_id: undefined,
    district_id: undefined,
    pincode: "",
    site_latitude: undefined,
    site_longitude: undefined,
    site_priority: "medium",
    pr_number: "",
    pr_date: "",
    po_number: "",
    po_date: "",
    po_files: [],
    is_po_no_shared_at_allocation: false,
    technician_name: "",
    technician_number: "",
    fse_name: "",
    fse_number: "",
    aom_name: "",
    aom_number: "",
    client_project_name: "",
    customer_requirement: "",
    site_information1: "",
    site_information2: "",
    site_information3: "",
    contact_person: "",
    contact_number: "",
    site_engineer_userid: undefined,
    ta_userid: undefined,
    mis_userid: undefined,
    report_designer_userid: undefined,
    validator_userid: undefined,
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [submitting, setSubmitting] = useState(false)
  const [pendingPoFiles, setPendingPoFiles] = useState<File[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [taUsers, setTaUsers] = useState<{ id: number; label: string; value: number }[]>([])
  const [misUsers, setMisUsers] = useState<{ id: number; label: string; value: number }[]>([])
  const [stepValidation, setStepValidation] = useState({
    1: false,
    2: false,
    3: false,
  })
  const [selectedState, setSelectedState] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedDistrict, setSelectedDistrict] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [selectedProjectOption, setSelectedProjectOption] = useState<{ id: number; label: string; value: string | number } | null>(null)
  const [visibleSiteInfoFields, setVisibleSiteInfoFields] = useState(1)

  // Country codes
  const [technicianCountryCode, setTechnicianCountryCode] = useState("+91")
  const [fseCountryCode, setFseCountryCode] = useState("+91")
  const [aomCountryCode, setAomCountryCode] = useState("+91")
  const [contactCountryCode, setContactCountryCode] = useState("+91")

  const countryOptions = useMemo(
    () =>
      countries.map((country, index) => ({
        id: index + 1,
        label: `${country.flag} ${country.code}`,
        value: country.code,
      })),
    []
  )

  const steps = [
    {
      number: 1,
      title: "Site Info",
      description: "Identification, location, and additional details",
      icon: LuInfo,
    },
    {
      number: 2,
      title: "Team & Contact",
      description: "Team members and contact",
      icon: LuUser,
    },
    {
      number: 3,
      title: "Purchase Order",
      description: "PO and PR details",
      icon: LuFileText,
    },
  ]

  // Reset form when modal opens/closes
  useEffect(() => {
    setFormData({
      site_id: "",
      project_id: 0,
      activity_id: undefined,
      reference_number: "",
      client_allocation_date: "",
      site_name: "",
      site_address: "",
      opco_name: "",
      opco_id: "",
      state_circle_id: undefined,
      district_id: undefined,
      pincode: "",
      site_latitude: undefined,
      site_longitude: undefined,
      site_priority: "medium",
      pr_number: "",
      pr_date: "",
      po_number: "",
      po_date: "",
      po_files: [],
      is_po_no_shared_at_allocation: false,
      technician_name: "",
      technician_number: "",
      fse_name: "",
      fse_number: "",
      aom_name: "",
      aom_number: "",
      client_project_name: "",
      customer_requirement: "",
      site_information1: "",
      site_information2: "",
      site_information3: "",
      contact_person: "",
      contact_number: "",
    })
    setErrors({})
    setSubmitting(false)
    setCurrentStep(1)
    setStepValidation({ 1: false, 2: false, 3: false })
    setSelectedState(null)
    setSelectedDistrict(null)
    setSelectedProjectOption(null)
    setVisibleSiteInfoFields(1)
    setTechnicianCountryCode("+91")
    setFseCountryCode("+91")
    setAomCountryCode("+91")
    setContactCountryCode("+91")
    setPendingPoFiles([])
  }, [])


  // Update form data
  const updateFormData = (field: keyof AddSiteData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }

    // Revalidate current step
    setTimeout(validateCurrentStep, 0)
  }

  const getDisplayFileName = (value: string) => {
    try {
      const cleanedValue = value.split("?")[0]
      const fileName = cleanedValue.split("/").pop()
      return fileName ? decodeURIComponent(fileName) : value
    } catch {
      return value
    }
  }

  const handlePoFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || [])
    if (!selectedFiles.length) {
      return
    }

    setPendingPoFiles((prev) => [...prev, ...selectedFiles])
    event.target.value = ""
  }

  const removePendingPoFile = (index: number) => {
    setPendingPoFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index))
  }

  const removeExistingPoFile = (index: number) => {
    const currentPoFiles = formData.po_files || []
    updateFormData(
      "po_files",
      currentPoFiles.filter((_, fileIndex) => fileIndex !== index)
    )
  }

  const createSite = async (siteData: AddSiteData) => {
    try {
      const response = await axiosInstance.post('/survey/sites', siteData);
      if (response.data.success) {
        console.log('Site created successfully');
        navigate(`/sites/${response.data.data.site.id}`)
      } else {
        console.error('Failed to create site:', response.data.message);
      }
    } catch (error) {
      console.error('Error creating site:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axiosInstance.get('/activities/list', {
        params: { limit: 100, status: 'ACTIVE' }
      });

      if (response.data.success) {
        setActivities(response.data.data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  useEffect(() => {
    fetchActivities()
  }, [])

  // Fetch TA and MIS users when project changes
  useEffect(() => {
    if (!formData.project_id) return
    axiosInstance.get(`/survey/users/role/5`).then((res) => {
      if (res.data.success) {
        const users = (res.data.data.users || []).map((u: any) => ({
          id: u.id, label: `${u.first_name} ${u.last_name}`, value: u.id
        }))
        setTaUsers(users)
      }
    })
    axiosInstance.get(`/survey/users/role/6`).then((res) => {
      if (res.data.success) {
        const users = (res.data.data.users || []).map((u: any) => ({
          id: u.id, label: `${u.first_name} ${u.last_name}`, value: u.id
        }))
        setMisUsers(users)
      }
    })
  }, [formData.project_id])

  const activeActivities = activities.filter((activity) => activity.status === "ACTIVE")

  // Validate phone number with country code
  const validatePhoneNumber = (phone: string, countryCode: string): boolean => {
    if (!phone || !phone.trim()) return true; // Optional field
    const fullNumber = countryCode + phone.trim();
    return validator.isMobilePhone(phone, 'any', { strictMode: false });
  }

  // Validate specific step
  const validateStep = (step: number) => {
    const newErrors: { [key: string]: string } = {}
    let isValid = true

    switch (step) {
      case 1:
        // Site Info validation (basic + location + additional)
        if (!formData.site_id.trim()) {
          newErrors.site_id = "Site ID is required"
          isValid = false
        } else if (formData.site_id.trim().length < 3) {
          newErrors.site_id = "Site ID must be at least 3 characters"
          isValid = false
        } else if (!/^[A-Z0-9_-]+$/i.test(formData.site_id)) {
          newErrors.site_id = "Site ID must contain only alphanumeric characters, hyphens, and underscores"
          isValid = false
        }

        // if (!formData.project_id) {
        //   newErrors.project_id = "Project selection is required"
        //   isValid = false
        // }

        if (formData.site_latitude !== undefined && formData.site_latitude !== null) {
          const lat = Number(formData.site_latitude)
          if (isNaN(lat) || lat < 6 || lat > 38) {
            newErrors.site_latitude = "Latitude must be within India range (6 to 38)"
            isValid = false
          }
        }

        if (formData.site_longitude !== undefined && formData.site_longitude !== null) {
          const lng = Number(formData.site_longitude)
          if (isNaN(lng) || lng < 68 || lng > 98) {
            newErrors.site_longitude = "Longitude must be within India range (68 to 98)"
            isValid = false
          }
        }

        if (formData.pincode && formData.pincode.trim()) {
          if (!validator.isPostalCode(formData.pincode.trim(), 'any')) {
            newErrors.pincode = "Invalid pincode format"
            isValid = false
          }
        }

        if (formData.site_name && formData.site_name.trim().length < 2) {
          newErrors.site_name = "Site name must be at least 2 characters"
          isValid = false
        }

        if (formData.site_address && formData.site_address.trim().length < 5) {
          newErrors.site_address = "Site address must be at least 5 characters"
          isValid = false
        }
        break

      case 2:
        // Team & Contact validation
        if (!formData.ta_userid) {
          newErrors.ta_userid = "Technical Associate (TA) is required"
          isValid = false
        }

        if (!formData.mis_userid) {
          newErrors.mis_userid = "MIS is required"
          isValid = false
        }

        if (formData.contact_number && formData.contact_number.trim()) {
          if (!validatePhoneNumber(formData.contact_number, contactCountryCode)) {
            newErrors.contact_number = "Invalid contact number format"
            isValid = false
          }
        }

        if (formData.technician_number && formData.technician_number.trim()) {
          if (!validatePhoneNumber(formData.technician_number, technicianCountryCode)) {
            newErrors.technician_number = "Invalid technician number format"
            isValid = false
          }
        }

        if (formData.fse_number && formData.fse_number.trim()) {
          if (!validatePhoneNumber(formData.fse_number, fseCountryCode)) {
            newErrors.fse_number = "Invalid FSE number format"
            isValid = false
          }
        }

        if (formData.aom_number && formData.aom_number.trim()) {
          if (!validatePhoneNumber(formData.aom_number, aomCountryCode)) {
            newErrors.aom_number = "Invalid AOM number format"
            isValid = false
          }
        }

        if (formData.technician_name && formData.technician_name.trim().length < 2) {
          newErrors.technician_name = "Technician name must be at least 2 characters"
          isValid = false
        }

        if (formData.fse_name && formData.fse_name.trim().length < 2) {
          newErrors.fse_name = "FSE name must be at least 2 characters"
          isValid = false
        }

        if (formData.aom_name && formData.aom_name.trim().length < 2) {
          newErrors.aom_name = "AOM name must be at least 2 characters"
          isValid = false
        }

        if (formData.contact_person && formData.contact_person.trim().length < 2) {
          newErrors.contact_person = "Contact person name must be at least 2 characters"
          isValid = false
        }
        break

      case 3:
        // Purchase order validation
        if (formData.client_allocation_date && formData.client_allocation_date.trim()) {
          if (!validator.isDate(formData.client_allocation_date, { format: 'YYYY-MM-DD', strictMode: true })) {
            newErrors.client_allocation_date = "Please use YYYY-MM-DD format"
            isValid = false
          }
        }

        if (formData.pr_date && formData.pr_date.trim()) {
          if (!validator.isDate(formData.pr_date, { format: 'YYYY-MM-DD', strictMode: true })) {
            newErrors.pr_date = "Please use YYYY-MM-DD format"
            isValid = false
          }
        }

        if (formData.po_date && formData.po_date.trim()) {
          if (!validator.isDate(formData.po_date, { format: 'YYYY-MM-DD', strictMode: true })) {
            newErrors.po_date = "Please use YYYY-MM-DD format"
            isValid = false
          }
        }
        break


    }

    setErrors((prev) => ({ ...prev, ...newErrors }))
    setStepValidation((prev) => ({ ...prev, [step]: isValid }))
    return isValid
  }

  // Validate current step
  const validateCurrentStep = () => {
    return validateStep(currentStep)
  }

  // Handle step navigation
  const goToStep = (step: number) => {
    if (step > currentStep) {
      if (!validateCurrentStep()) {
        return
      }
    }

    if (step >= 1 && step <= 3) {
      setCurrentStep(step)
    }
  }

  // Handle next button
  const handleNext = () => {
    if (validateCurrentStep() && currentStep < 4) {
      setCurrentStep(currentStep + 1)
    }
  }

  // Handle previous button
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Handle form submission
  const handleSubmit = async () => {
    // Validate all steps
    const step1Valid = validateStep(1)
    const step2Valid = validateStep(2)
    const step3Valid = validateStep(3)

    if (!step1Valid || !step2Valid || !step3Valid) {
      return
    }

    setSubmitting(true)
    try {
      const uploadedPoFiles = pendingPoFiles.length
        ? await uploadFilesWithPresignedUrls(pendingPoFiles)
        : []
      const poFiles = Array.from(new Set([...(formData.po_files || []), ...uploadedPoFiles]))

      const submitData: AddSiteData = {
        ...formData,
        site_id: formData.site_id.trim(),
        activity_id: formData.activity_id || undefined,
        reference_number: formData.reference_number?.trim() || undefined,
        site_name: formData.site_name?.trim() || undefined,
        site_address: formData.site_address?.trim() || undefined,
        opco_name: formData.opco_name?.trim() || undefined,
        opco_id: formData.opco_id?.trim() || undefined,
        state_circle_id: formData.state_circle_id || undefined,
        district_id: formData.district_id || undefined,
        pincode: formData.pincode?.trim() || undefined,
        pr_number: formData.pr_number?.trim() || undefined,
        po_number: formData.po_number?.trim() || undefined,
        technician_name: formData.technician_name?.trim() || undefined,
        technician_number: formData.technician_number?.trim() ? technicianCountryCode + formData.technician_number.trim() : undefined,
        fse_name: formData.fse_name?.trim() || undefined,
        fse_number: formData.fse_number?.trim() ? fseCountryCode + formData.fse_number.trim() : undefined,
        aom_name: formData.aom_name?.trim() || undefined,
        aom_number: formData.aom_number?.trim() ? aomCountryCode + formData.aom_number.trim() : undefined,
        client_project_name: formData.client_project_name?.trim() || undefined,
        customer_requirement: formData.customer_requirement?.trim() || undefined,
        site_information1: formData.site_information1?.trim() || undefined,
        site_information2: formData.site_information2?.trim() || undefined,
        site_information3: formData.site_information3?.trim() || undefined,
        contact_person: formData.contact_person?.trim() || undefined,
        contact_number: formData.contact_number?.trim() ? contactCountryCode + formData.contact_number.trim() : undefined,
        site_latitude: formData.site_latitude || undefined,
        site_longitude: formData.site_longitude || undefined,
        po_files: poFiles,
      }

      await createSite(submitData)
    } catch (error) {
      console.error("Failed to create site:", error)
    } finally {
      setSubmitting(false)
    }
  }

  // Check if step is accessible
  const isStepAccessible = (step: number) => {
    if (step === 1) return true
    if (step === 2) return stepValidation[1]
    if (step === 3) return stepValidation[1] && stepValidation[2]
    return false
  }

  // Handle state selection
  const handleStateChange = (option: { id: number; label: string; value: string | number } | null) => {
    setSelectedState(option)
    setSelectedDistrict(null) // Clear district when state changes
    updateFormData("state_circle_id", option ? option.id : undefined)
    updateFormData("district_id", undefined)
  }

  // Handle district selection
  const handleDistrictChange = (option: { id: number; label: string; value: string | number } | null) => {
    setSelectedDistrict(option)
    updateFormData("district_id", option ? option.id : undefined)
  }

  return (
    <>

      {/* Offcanvas */}
      <div className="bg-white p-4 rounded border">
        {/* Header - Responsive */}
        <div className="flex items-center justify-between border-b border-gray-200 flex-shrink-0">
          <div className="min-w-0 flex-1 mb-2">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 truncate">Add New Site</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
              Create a new site with complete project details
            </p>
          </div>
        </div>

        {/* Stepper - Responsive */}
        <div className="flex items-center justify-between w-full bg-white border-b border-gray-200 px-2 sm:px-6 py-3 sm:py-4 overflow-x-auto">
          <div className="flex items-center gap-6 min-w-max mx-auto">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isActive = currentStep === step.number
              const isCompleted = stepValidation[step.number as 1 | 2 | 3]
              const isAccessible = isStepAccessible(step.number)

              return (
                <div key={step.number} className="flex items-center">
                  {/* Step Circle */}
                  <button
                    onClick={() => goToStep(step.number)}
                    disabled={!isAccessible}
                    className={`relative flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 flex-shrink-0
              ${isActive
                        ? "border-blue-800 bg-blue-800 text-white"
                        : isCompleted
                          ? "border-green-600 bg-green-600 text-white"
                          : isAccessible
                            ? "border-gray-300 bg-white text-gray-600 hover:border-gray-400"
                            : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                      }`}
                  >
                    {isCompleted && !isActive ? (
                      <LuCheck className="w-5 h-5" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </button>

                  {/* Step Info */}
                  <div className="ml-3 hidden md:block">
                    <p
                      className={`text-sm font-medium whitespace-nowrap ${isActive
                          ? "text-blue-800"
                          : isCompleted
                            ? "text-green-600"
                            : isAccessible
                              ? "text-gray-900"
                              : "text-gray-400"
                        }`}
                    >
                      {step.title}
                    </p>
                    <p
                      className={`text-xs whitespace-nowrap ${isActive
                          ? "text-blue-500"
                          : isCompleted
                            ? "text-green-500"
                            : isAccessible
                              ? "text-gray-500"
                              : "text-gray-400"
                        }`}
                    >
                      {step.description}
                    </p>
                  </div>

                  {/* Mobile title */}
                  <div className="ml-2 md:hidden">
                    <p
                      className={`text-xs font-medium whitespace-nowrap ${isActive
                          ? "text-blue-800"
                          : isCompleted
                            ? "text-green-600"
                            : isAccessible
                              ? "text-gray-900"
                              : "text-gray-400"
                        }`}
                    >
                      {step.title}
                    </p>
                  </div>

                  {/* Connector */}
                  {index < steps.length - 1 && (
                    <div
                      className={`w-10 sm:w-20 h-0.5 mx-3 ${stepValidation[step.number as 1 | 2 | 3]
                          ? "bg-green-300"
                          : "bg-gray-200"
                        }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>


        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Step 1: Site Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Basic Site Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuInfo className="w-4 sm:w-5 h-4 sm:h-5 text-blue-800" />
                  Basic Site Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site ID *</label>
                    <input
                      type="text"
                      value={formData.site_id}
                      onChange={(e) => updateFormData("site_id", e.target.value)}
                      placeholder="Enter unique site identifier (e.g., SITE001)"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_id ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.site_id && <p className="mt-1 text-xs text-red-600">{errors.site_id}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Activity</label>
                      <SelectWithSearch2
                        value={formData.activity_id ? { id: formData.activity_id, label: "", value: formData.activity_id } : null}
                        onChange={(option) => updateFormData("activity_id", option ? option.id : undefined)}
                        apiEndpoint="/activities/list"
                        dataKey="activities"
                        labelKey="activity_name"
                        valueKey="id"
                        displayFormat={(item) => item.activity_name}
                        extraParams={{ status: "ACTIVE" }}
                        placeholder="Select an activity"
                        minSearchLength={0}
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
                      <input
                        type="text"
                        value={formData.reference_number || ""}
                        onChange={(e) => updateFormData("reference_number", e.target.value)}
                        placeholder="Enter reference number"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Client Allocation Date</label>
                    <input
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formData.client_allocation_date || ""}
                      onChange={(e) => updateFormData("client_allocation_date", e.target.value)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.client_allocation_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.client_allocation_date && <p className="mt-1 text-xs text-red-600">{errors.client_allocation_date}</p>}
                  </div>
                </div>
              </div>

              {/* Project Assignment */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuNetwork className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  Project Assignment
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Project *</label>
                  <SelectWithSearch2
                    value={selectedProjectOption}
                    onChange={(option) => {
                      setSelectedProjectOption(option)
                      updateFormData("project_id", option ? option.id : 0)
                    }}
                    apiEndpoint="/projects/list"
                    dataKey="projects"
                    labelKey="project_name"
                    valueKey="id"
                    displayFormat={(item) => item.project_name}
                    extraParams={{ status: "ACTIVE" }}
                    placeholder="Select a project"
                    minSearchLength={0}
                    disabled={submitting}
                  />
                  {errors.project_id && <p className="mt-1 text-xs text-red-600">{errors.project_id}</p>}
                </div>
              </div>

              {/* Location Details */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuMapPin className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                  Site Location
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                      <input
                        type="text"
                        value={formData.site_name || ""}
                        onChange={(e) => updateFormData("site_name", e.target.value)}
                        placeholder="Enter site name"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_name ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={submitting}
                      />
                      {errors.site_name && <p className="mt-1 text-xs text-red-600">{errors.site_name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Priority</label>
                      <select
                        value={formData.site_priority || "medium"}
                        onChange={(e) => updateFormData("site_priority", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={submitting}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Address</label>
                    <textarea
                      value={formData.site_address || ""}
                      onChange={(e) => updateFormData("site_address", e.target.value)}
                      placeholder="Enter complete site address"
                      rows={3}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical ${errors.site_address ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.site_address && <p className="mt-1 text-xs text-red-600">{errors.site_address}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                      <SelectWithSearch
                        value={selectedState}
                        onChange={handleStateChange}
                        placeholder="Select state"
                        disabled={submitting}
                        apiEndpoint="/state-circle/list"
                        dataKey="states"
                        labelKey="state_name"
                        displayFormat={(item) => `${item.state_name}`}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                      <SelectWithSearch
                        value={selectedDistrict}
                        onChange={handleDistrictChange}
                        placeholder="Select district"
                        disabled={submitting || !selectedState}
                        apiEndpoint="/state-circle"
                        dataKey="districts"
                        dependencyId={selectedState?.id}
                        labelKey="name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                      <input
                        type="text"
                        value={formData.pincode || ""}
                        onChange={(e) => updateFormData("pincode", e.target.value)}
                        placeholder="Enter pincode"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.pincode ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={submitting}
                      />
                      {errors.pincode && <p className="mt-1 text-xs text-red-600">{errors.pincode}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* OpCo Details */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuBuilding className="w-4 sm:w-5 h-4 sm:h-5 text-orange-600" />
                  Operating Company Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">OpCo Name</label>
                    <input
                      type="text"
                      value={formData.opco_name || ""}
                      onChange={(e) => updateFormData("opco_name", e.target.value)}
                      placeholder="Enter operating company name"
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">OpCo ID</label>
                    <input
                      type="text"
                      value={formData.opco_id || ""}
                      onChange={(e) => updateFormData("opco_id", e.target.value)}
                      placeholder="Enter operating company ID"
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>
              </div>

              {/* Coordinates */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuGlobe className="w-4 sm:w-5 h-4 sm:h-5 text-blue-800" />
                  Site GPS Coordinates
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.site_latitude || ""}
                      onChange={(e) => updateFormData("site_latitude", e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder="Enter latitude (e.g., 28.6139)"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_latitude ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.site_latitude && <p className="mt-1 text-xs text-red-600">{errors.site_latitude}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={formData.site_longitude || ""}
                      onChange={(e) => updateFormData("site_longitude", e.target.value ? parseFloat(e.target.value) : undefined)}
                      placeholder="Enter longitude (e.g., 77.2090)"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.site_longitude ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.site_longitude && <p className="mt-1 text-xs text-red-600">{errors.site_longitude}</p>}
                  </div>
                </div>

                <div className="mt-3 flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <LuInfo className="w-4 h-4 text-blue-800 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p>GPS coordinates help in precise site location identification and navigation.</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuClipboard className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-600" />
                  Additional Site Information
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Requirement</label>
                    <textarea
                      value={formData.customer_requirement || ""}
                      onChange={(e) => updateFormData("customer_requirement", e.target.value)}
                      placeholder="Enter customer requirements"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 1</label>
                    <textarea
                      value={formData.site_information1 || ""}
                      onChange={(e) => updateFormData("site_information1", e.target.value)}
                      placeholder="Enter additional site information"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                      disabled={submitting}
                    />
                  </div>

                  {visibleSiteInfoFields >= 2 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 2</label>
                      <textarea
                        value={formData.site_information2 || ""}
                        onChange={(e) => updateFormData("site_information2", e.target.value)}
                        placeholder="Enter additional site information"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                        disabled={submitting}
                      />
                    </div>
                  )}

                  {visibleSiteInfoFields >= 3 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Site Information 3</label>
                      <textarea
                        value={formData.site_information3 || ""}
                        onChange={(e) => updateFormData("site_information3", e.target.value)}
                        placeholder="Enter additional site information"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                        disabled={submitting}
                      />
                    </div>
                  )}

                  {visibleSiteInfoFields < 3 && (
                    <div className="flex justify-start">
                      <button
                        type="button"
                        onClick={() => setVisibleSiteInfoFields(prev => prev + 1)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-800 text-white text-sm font-medium rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={submitting}
                      >
                        <LuPlus className="w-4 h-4" />
                        Add More
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Team & Contact */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Team Members */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuUser className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  Team Assignment
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Technical Associate (TA) <span className="text-red-500">*</span></label>
                      <SelectWithSearch
                        placeholder="Select TA"
                        value={taUsers.find(u => u.id === formData.ta_userid) || null}
                        onChange={(option) => updateFormData("ta_userid", option?.id)}
                        options={taUsers}
                        disabled={submitting}
                      />
                      {errors.ta_userid && <p className="mt-1 text-xs text-red-600">{errors.ta_userid}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">MIS <span className="text-red-500">*</span></label>
                      <SelectWithSearch
                        placeholder="Select MIS"
                        value={misUsers.find(u => u.id === formData.mis_userid) || null}
                        onChange={(option) => updateFormData("mis_userid", option?.id)}
                        options={misUsers}
                        disabled={submitting}
                      />
                      {errors.mis_userid && <p className="mt-1 text-xs text-red-600">{errors.mis_userid}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Technician Name</label>
                      <input
                        type="text"
                        value={formData.technician_name || ""}
                        onChange={(e) => updateFormData("technician_name", e.target.value)}
                        placeholder="Enter technician name"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.technician_name ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={submitting}
                      />
                      {errors.technician_name && <p className="mt-1 text-xs text-red-600">{errors.technician_name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Technician Number</label>
                      <div className="flex gap-2">
                        <SelectWithSearch
                          className="w-28 sm:w-32"
                          placeholder="Code"
                          value={countryOptions.find(option => option.value === technicianCountryCode) || null}
                          onChange={(option) => setTechnicianCountryCode(option?.value?.toString() || "+91")}
                          options={countryOptions}
                          disabled={submitting}
                        />
                        <input
                          type="tel"
                          value={formData.technician_number || ""}
                          onChange={(e) => updateFormData("technician_number", e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="Enter technician contact number"
                          className={`flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.technician_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                            }`}
                          disabled={submitting}
                        />
                      </div>
                      {errors.technician_number && <p className="mt-1 text-xs text-red-600">{errors.technician_number}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">FSE Name</label>
                      <input
                        type="text"
                        value={formData.fse_name || ""}
                        onChange={(e) => updateFormData("fse_name", e.target.value)}
                        placeholder="Enter FSE name"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.fse_name ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={submitting}
                      />
                      {errors.fse_name && <p className="mt-1 text-xs text-red-600">{errors.fse_name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">FSE Number</label>
                      <div className="flex gap-2">
                        <SelectWithSearch
                          className="w-28 sm:w-32"
                          placeholder="Code"
                          value={countryOptions.find(option => option.value === fseCountryCode) || null}
                          onChange={(option) => setFseCountryCode(option?.value?.toString() || "+91")}
                          options={countryOptions}
                          disabled={submitting}
                        />
                        <input
                          type="tel"
                          value={formData.fse_number || ""}
                          onChange={(e) => updateFormData("fse_number", e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="Enter FSE contact number"
                          className={`flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.fse_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                            }`}
                          disabled={submitting}
                        />
                      </div>
                      {errors.fse_number && <p className="mt-1 text-xs text-red-600">{errors.fse_number}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">AOM Name</label>
                      <input
                        type="text"
                        value={formData.aom_name || ""}
                        onChange={(e) => updateFormData("aom_name", e.target.value)}
                        placeholder="Enter AOM name"
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.aom_name ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={submitting}
                      />
                      {errors.aom_name && <p className="mt-1 text-xs text-red-600">{errors.aom_name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">AOM Number</label>
                      <div className="flex gap-2">
                        <SelectWithSearch
                          className="w-28 sm:w-32"
                          placeholder="Code"
                          value={countryOptions.find(option => option.value === aomCountryCode) || null}
                          onChange={(option) => setAomCountryCode(option?.value?.toString() || "+91")}
                          options={countryOptions}
                          disabled={submitting}
                        />
                        <input
                          type="tel"
                          value={formData.aom_number || ""}
                          onChange={(e) => updateFormData("aom_number", e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="Enter AOM contact number"
                          className={`flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.aom_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                            }`}
                          disabled={submitting}
                        />
                      </div>
                      {errors.aom_number && <p className="mt-1 text-xs text-red-600">{errors.aom_number}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuPhone className="w-4 sm:w-5 h-4 sm:h-5 text-green-600" />
                  Site Owner Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person</label>
                    <input
                      type="text"
                      value={formData.contact_person || ""}
                      onChange={(e) => updateFormData("contact_person", e.target.value)}
                      placeholder="Enter contact person name"
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.contact_person ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.contact_person && <p className="mt-1 text-xs text-red-600">{errors.contact_person}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                    <div className="flex gap-2">
                      <SelectWithSearch
                        className="w-28 sm:w-32"
                        placeholder="Code"
                        value={countryOptions.find(option => option.value === contactCountryCode) || null}
                        onChange={(option) => setContactCountryCode(option?.value?.toString() || "+91")}
                        options={countryOptions}
                        disabled={submitting}
                      />
                      <input
                        type="tel"
                        value={formData.contact_number || ""}
                        onChange={(e) => updateFormData("contact_number", e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="Enter contact number"
                        className={`flex-1 px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.contact_number ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={submitting}
                      />
                    </div>
                    {errors.contact_number && <p className="mt-1 text-xs text-red-600">{errors.contact_number}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Purchase Order Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Purchase Request */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuFileText className="w-4 sm:w-5 h-4 sm:h-5 text-blue-800" />
                  Purchase Request Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PR Number</label>
                    <input
                      type="text"
                      value={formData.pr_number || ""}
                      onChange={(e) => updateFormData("pr_number", e.target.value)}
                      placeholder="Enter PR number"
                      className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PR Date</label>
                    <input
                      type="date"
                      max={new Date().toISOString().split("T")[0]}
                      value={formData.pr_date || ""}
                      onChange={(e) => updateFormData("pr_date", e.target.value)}
                      className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.pr_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                        }`}
                      disabled={submitting}
                    />
                    {errors.pr_date && <p className="mt-1 text-xs text-red-600">{errors.pr_date}</p>}
                  </div>
                </div>
              </div>

              {/* Purchase Order */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <LuClipboard className="w-4 sm:w-5 h-4 sm:h-5 text-purple-600" />
                  Purchase Order Details
                </h3>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PO Number</label>
                      <input
                        type="text"
                        value={formData.po_number || ""}
                        onChange={(e) => updateFormData("po_number", e.target.value)}
                        placeholder="Enter PO number"
                        className="w-full px-3 py-2 border border-gray-300 rounded  focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={submitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">PO Date</label>
                      <input
                        type="date"
                        max={new Date().toISOString().split("T")[0]}
                        value={formData.po_date || ""}
                        onChange={(e) => updateFormData("po_date", e.target.value)}
                        className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.po_date ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-gray-300"
                          }`}
                        disabled={submitting}
                      />
                      {errors.po_date && <p className="mt-1 text-xs text-red-600">{errors.po_date}</p>}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_po_no_shared_at_allocation"
                      checked={formData.is_po_no_shared_at_allocation || false}
                      onChange={(e) => updateFormData("is_po_no_shared_at_allocation", e.target.checked)}
                      className="w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded  focus:ring-blue-500"
                      disabled={submitting}
                    />
                    <label htmlFor="is_po_no_shared_at_allocation" className="text-sm font-medium text-gray-700">
                      PO number shared at allocation
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">PO Files</label>
                    <input
                      type="file"
                      multiple
                      onChange={handlePoFilesChange}
                      className="w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-2 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-800 hover:file:bg-blue-100"
                      disabled={submitting}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Files are uploaded using presigned URLs when you submit this form.
                    </p>
                  </div>

                  {(formData.po_files?.length || 0) > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Existing PO Files</p>
                      <div className="space-y-2">
                        {(formData.po_files || []).map((fileUrl, index) => (
                          <div key={`${fileUrl}-${index}`} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sm text-blue-700 hover:underline truncate pr-3"
                            >
                              {getDisplayFileName(fileUrl)}
                            </a>
                            <button
                              type="button"
                              onClick={() => removeExistingPoFile(index)}
                              className="text-xs text-red-600 hover:text-red-700"
                              disabled={submitting}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pendingPoFiles.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">New Files To Upload</p>
                      <div className="space-y-2">
                        {pendingPoFiles.map((file, index) => (
                          <div key={`${file.name}-${file.size}-${index}`} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                            <span className="text-sm text-gray-700 truncate pr-3">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removePendingPoFile(index)}
                              className="text-xs text-red-600 hover:text-red-700"
                              disabled={submitting}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}


        </div>

        {/* Footer - Fixed */}
        <div className="border-t border-gray-200 bg-white p-4 sm:p-6 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Progress indicator */}
            <div className="flex items-center gap-2 text-sm text-gray-600 order-2 sm:order-1">
              <span>Step {currentStep} of {steps.length}</span>
              <div className="w-24 bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-800 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / steps.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto">
              {currentStep > 1 && (
                <button
                  onClick={handlePrevious}
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  Previous
                </button>
              )}

              {currentStep < steps.length ? (
                <button
                  onClick={handleNext}
                  disabled={submitting || !stepValidation[currentStep as 1 | 2 | 3]}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  Next
                  <LuChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !Object.values(stepValidation).slice(0, 3).every(Boolean)}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 sm:flex-none justify-center"
                >
                  {submitting ? (
                    <>
                      <LuLoader className="w-4 h-4 animate-spin" />
                      Creating Site...
                    </>
                  ) : (
                    <>
                      <LuPlus className="w-4 h-4" />
                      Create Site
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={() => {
          setShowCloseModal(false);
          onClose();
        }}
      /> */}

    </>
  );
};

export default CreateSite;
